import { gql, useQuery } from "@apollo/client";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { dataMap } from "src/Components/Segments/DashboardSideBarSegments/LeadCardV2/LeadCardTopInfoV2";
import { formatLeadFiltersPayload } from "src/Components/Segments/LeadsFilterV2/shared";
import { AppText, FlexDiv } from "src/Components/UI";
import { PhoenixIcon, PhoenixInput } from "src/Components/UI/Phoenix";
import { LeadCardContext, LeadFilterContext, ModalContext, SelfSourceLeadContext } from "src/context";
import { plus, search, xIcon } from "src/images/NewDesign";
import { MixpanelActions } from "src/services/mixpanel";
import { OptionItem } from "src/types/genericTypes";
import { formatDateTime } from "src/utils/format";
import { useDebounce } from "src/utils/hooks";
import { isDateTimeString } from "src/utils/misc";
import { theme } from "src/utils/theme";
import styled from "styled-components";
import { SearchModuleFilters } from "./SearchModuleFilters";

const FETCH_TOUCHED_LEADS = gql`
  query fetchTouchedLeads($searchText: String, $skip: Int, $take: Int, $lead_filter: LeadFilter) {
    fetchTouchedLeads(searchText: $searchText, skip: $skip, take: $take, lead_filter: $lead_filter) {
      id
      business_name
      channel
      city
      computed_mrr
      country
      first_name
      last_name
      full_name
      industry
      sub_industry
      lead_source
      primary_email
      primary_phone_number
      state
      address
      zip
      current_close_date
      visible_custom_fields {
        id
        key
        computed_value
      }
    }
  }
`;

const takeVal = 16;

/*
 * product would like business name to be the first column regardless of the order set by user in the pinned fields on lead card
 */
const RearangeUserColumns = (leadCardFields: OptionItem[]): OptionItem[] => {
  // required field, so we can safely assume it will always be in the leadCardFields array
  const businessNameField = leadCardFields.find((field) => field.value === "BUSINESS_NAME");

  const otherFields = leadCardFields.filter((field) => field.value !== "BUSINESS_NAME");
  return [businessNameField, ...otherFields] as OptionItem[];
};

export const SearchLeadModule: React.FC = () => {
  const { leadSystemFilter } = useContext(LeadFilterContext);
  const { leadCardFields } = useContext(LeadCardContext);
  const formatedLeadCardFields: OptionItem[] = useMemo(() => RearangeUserColumns(leadCardFields), [leadCardFields]);
  const { resetSelfSourcedLead } = useContext(SelfSourceLeadContext);
  const { setSelfSource } = useContext(ModalContext);

  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");
  useDebounce(
    () => {
      setDebouncedSearchValue(searchValue);
      setSkip(0);
      setHasMore(true);
      setTableData([]);
    },
    [searchValue],
    500,
  );

  const [showModule, setShowModule] = useState(false);

  const [skip, setSkip] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const [tableData, setTableData] = useState<any[]>([]);

  const [localLeadSystemFilter, setLocalLeadSystemFilter] = useState(leadSystemFilter);

  const handleClose = useCallback(() => {
    setShowModule(false);
    setSearchValue("");
    setSkip(0);
    setTableData([]);
    setHasMore(true);
  }, [setShowModule, setSearchValue]);
  useEffect(() => {
    if (!!searchValue.length) {
      setShowModule(true);
    }
  }, [searchValue]);

  const onResetFilter = useCallback(() => {
    setSearchValue("");
    setSkip(0);
    setTableData([]);
    setHasMore(true);
  }, []);

  useEffect(() => {
    if (JSON.stringify(leadSystemFilter) === JSON.stringify(localLeadSystemFilter)) return;
    setLocalLeadSystemFilter(leadSystemFilter);
  }, [leadSystemFilter]);
  useEffect(() => {
    setSkip(0);
    setTableData([]);
    setHasMore(true);
  }, [localLeadSystemFilter]);

  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    loop: false,
    mode: "free-snap",
    renderMode: "performance",
    slides: {
      perView: "auto",
    },
  });

  /* The keen slider container has an opening animation effecting the width.
  Because of this the library calculated container width sometimes screws up. 
  Calling update after the animation plays guarentees the correct container width. */
  useEffect(() => {
    if (slider.current && showModule) {
      setTimeout(() => {
        slider.current?.update();
      }, 500);
    }
  }, [slider, showModule]);

  const leadFilterPayload = useMemo(() => formatLeadFiltersPayload(leadSystemFilter), [leadSystemFilter]);

  const handleShowMore = () => setSkip((prev) => prev + takeVal);

  useQuery(FETCH_TOUCHED_LEADS, {
    fetchPolicy: "no-cache",
    partialRefetch: false,
    skip: !showModule,
    variables: {
      searchText: debouncedSearchValue,
      lead_filter: leadFilterPayload,
      skip: skip,
      take: takeVal,
    },
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
    onCompleted({ fetchTouchedLeads }) {
      if (!fetchTouchedLeads) return;

      const incomingData = [...fetchTouchedLeads];
      const newList = [...tableData, ...incomingData];

      setTableData(newList);
      if (incomingData?.length < takeVal) setHasMore(false);
      else setHasMore(true);
    },
  });

  const getColumnData = useCallback(
    (header: keyof typeof dataMap) =>
      tableData?.map((row: any) => ({
        id: row.id,
        val: !!dataMap[header]
          ? row[dataMap[header]]
          : row.visible_custom_fields.find((vcf: any) => vcf.key === header)?.computed_value,
      })) || [],
    [tableData],
  );

  return (
    <ModuleContainer>
      {showModule && (
        <>
          <Blur onClick={handleClose} />
          <SearchContainer>
            <SearchHeader>
              <PhoenixIcon
                svg={xIcon}
                size={24}
                alt="close search"
                variant="brand"
                pointer
                style={{ marginLeft: "auto" }}
                onClick={handleClose}
              />

              <SearchModuleFilters onResetFilter={onResetFilter} />
            </SearchHeader>

            <InfiniteScroll
              dataLength={tableData.length}
              next={handleShowMore}
              hasMore={hasMore}
              loader={<></>}
              scrollableTarget="search-table"
            >
              <SearchTable ref={sliderRef} className="keen-slider" id="search-table">
                {formatedLeadCardFields?.map((h: { label: string; value: any }) => (
                  <Column key={h.label} className="keen-slider__slide">
                    <HeaderCell>
                      <AppText fontSize={12} fontWeight={600} lineHeight={18} color={theme.text.neutral.primary}>
                        {h.label}
                      </AppText>
                    </HeaderCell>
                    {getColumnData(h.value)?.map((leadInfo: { id: string; val: any }, i: number) => (
                      <Cell key={`${h}-${leadInfo.val}-${i}`}>
                        <CellText
                          fontSize={10}
                          fontWeight={400}
                          lineHeight={14}
                          color={theme.text.neutral.primary}
                          clickable={h.label === "Business name"}
                          onClick={() =>
                            h.label === "Business name" && window.open(`/lead-detail/${leadInfo.id}`, "_blank")
                          }
                        >
                          {h.label === "Business name" && !leadInfo.val
                            ? "N/A"
                            : isDateTimeString(leadInfo.val)
                            ? formatDateTime(leadInfo.val)
                            : leadInfo.val}
                        </CellText>
                      </Cell>
                    ))}
                  </Column>
                ))}
              </SearchTable>
            </InfiniteScroll>
            <FlexDiv justify="center" align="center" height="100%">
              <AppText
                fontSize={10}
                fontWeight={600}
                letterSpacing={1}
                lineHeight={16}
                color={theme.buttontext.brand_outline.default}
                uppercase
                style={{ cursor: "pointer", borderBottom: "none", userSelect: "none" }}
                onClick={() => {
                  if (tableData.length) {
                    window.open(`/system-view/my-leads?search=${encodeURIComponent(searchValue)}`, "_blank");
                  } else {
                    MixpanelActions.track("Add Self Sourced Lead", { type: "click", view: "Dashboard" });
                    resetSelfSourcedLead();
                    setSelfSource(true);
                    handleClose();
                  }
                }}
              >
                {tableData.length ? "View in My Leads" : "No leads found. Add Lead?"}
              </AppText>
            </FlexDiv>
          </SearchContainer>
        </>
      )}

      <FlexDiv gap={8} align="center" padding="0px 24px 0px 0px">
        <SearchLeadInput
          value={searchValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
          placeholder="Search Leads"
          height={32}
          displayNoContextText
          insideLeftElementOverride={
            <PhoenixIcon
              svg={search}
              size={16}
              color={theme.icon.brand.default}
              hoverColor={theme.icon.brand.default}
              style={{ paddingLeft: "20px", zIndex: 203, animation: "searchLeadStartup 0.5s ease-in-out forwards" }}
            />
          }
        />
        <AddLeadButton />
      </FlexDiv>
    </ModuleContainer>
  );
};

const ModuleContainer = styled.div`
  position: relative;
`;

const SearchContainer = styled.div`
  position: fixed;
  z-index: 201;

  display: flex;
  flex-direction: column;

  background-color: ${theme.fill.neutral.primary};

  animation: opening 0.35s ease-in-out forwards;
  @keyframes opening {
    0% {
      top: 18px;
      left: 96px;

      width: 432px;
      height: 40px;
      min-height: 40px;

      border-radius: 2px;
      overflow: hidden;
    }
    75% {
      height: 70%;
      min-height: 490px;
      overflow: hidden;
    }
    99% {
      overflow: hidden;
    }
    100% {
      top: 8px;
      left: 88px;

      width: 75%;
      height: 70%;
      min-height: 490px;

      border-radius: 8px;
      overflow: unset;
    }
  }
`;

const SearchHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  padding: 12px 12px 12px 8px;
`;

const Blur = styled.div`
  position: fixed;
  z-index: 200;
  top: 0;
  left: 0;

  width: 100vw;
  height: 100vh;

  overflow: hidden;

  animation: blur 0.2s ease-in-out forwards;
  @keyframes blur {
    0% {
      background: rgba(7, 23, 68, 0);
      backdrop-filter: blur(0px);
    }
    100% {
      background: rgba(7, 23, 68, 0.4);
      backdrop-filter: blur(5px);
    }
  }
`;

const SearchLeadInput = styled(PhoenixInput)`
  position: relative;
  z-index: 202;

  margin: 0px 0px 0px 24px;

  animation: searchLeadStartup 0.5s ease-in-out forwards;
  @keyframes searchLeadStartup {
    0% {
      opacity: 0.8;
      margin-top: 16px;
    }
    100% {
      opacity: 1;
      margin-top: 0px;
    }
  }
`;

const HeaderCell = styled.div`
  position: sticky;
  top: 0;

  display: flex;
  align-items: center;

  width: 165px;
  min-height: 40px;
  max-height: 40px;

  padding: 8px 16px;

  background-color: ${theme.fill.brand.secondary};
`;
const Cell = styled.div`
  display: flex;
  align-items: center;

  width: 165px;
  min-height: 40px;
  max-height: 40px;

  padding: 8px 16px;
`;

const SearchTable = styled.div`
  max-height: 52vh;

  overflow-x: hidden !important;
  overflow-y: auto !important;
`;

const Column = styled.div`
  min-width: 165px;
  max-width: 165px;
  height: fit-content;
  overflow: initial !important;

  & ${Cell} {
    :nth-child(even) {
      background-color: ${theme.fill.brand.tertiary};
    }
    :nth-child(odd) {
      background-color: ${theme.fill.neutral.primary};
    }
  }
`;

const CellText = styled(AppText)<{ clickable: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  :hover {
    cursor: ${(props) => (props.clickable ? "pointer" : "initial")};
    color: ${(props) => (props.clickable ? theme.text.brand.primary : theme.text.neutral.primary)};
  }
  transition: color 0.2s ease-in-out;
  border-bottom: none;
`;

const AddLeadButton: React.FC = () => {
  const { resetSelfSourcedLead } = useContext(SelfSourceLeadContext);
  const { setSelfSource } = useContext(ModalContext);

  const handleClick = useCallback(() => {
    MixpanelActions.track("Add Self Sourced Lead", { type: "click", view: "Dashboard" });
    resetSelfSourcedLead();
    setSelfSource(true);
  }, []);

  return (
    <AddLeadButtonContainer onClick={handleClick}>
      <PhoenixIcon
        size={16}
        svg={plus}
        alt="add lead"
        pointer
        color={theme.icon.neutral.inverse}
        hoverColor={theme.icon.neutral.inverse}
      />
    </AddLeadButtonContainer>
  );
};

const AddLeadButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border-radius: 4px;

  background-color: ${theme.buttonfill.brand.default};

  :hover {
    background-color: ${theme.buttonfill.brand.hover};
  }

  transition: background-color 0.15s ease;
  cursor: pointer;

  animation: addLeadStartup 0.5s ease-in-out forwards;
  @keyframes addLeadStartup {
    0% {
      opacity: 0.8;
      margin-top: 28px;
    }
    100% {
      opacity: 1;
      margin-top: 0px;
    }
  }
`;
